// Configuration file for Sales Guru React App
// Values are injected at build time from .env.<CLIENT> files

/**
 * Parse a boolean feature-flag env var defensively: only the explicit
 * strings 'true'/'false' (case-insensitive) count; anything else — unset,
 * empty, or garbage — resolves to the provided default so a bad .env value
 * can never crash or silently disable the app.
 */
export const parseBooleanEnvVar = (
  rawValue: string | undefined,
  defaultValue: boolean
): boolean => {
  const normalized = rawValue?.trim().toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return defaultValue;
};

const TRAINING_PATH = '/training/';
const PLAYBOOK_LIVE_PATH = '/training-live/';
const LOCAL_PLAYBOOK_LIVE_URL = 'ws://localhost:6342';

/**
 * Playbook Live sits beside KB training behind the same nginx
 * (`/training/` -> 6340, `/training-live/` -> 6342), so a tenant's Playbook
 * Live URL is its training URL with the path swapped. Deriving it means no
 * tenant needs a second env var; REACT_APP_PLAYBOOK_LIVE_URL still overrides.
 * A training URL without the `/training/` path (a local port) falls back to
 * the local Playbook Live port.
 */
export const resolvePlaybookLiveUrl = (
  explicitUrl: string | undefined,
  trainingUrl: string | undefined
): string => {
  if (explicitUrl?.trim()) return explicitUrl.trim();
  if (trainingUrl?.endsWith(TRAINING_PATH)) {
    return `${trainingUrl.slice(0, -TRAINING_PATH.length)}${PLAYBOOK_LIVE_PATH}`;
  }
  return LOCAL_PLAYBOOK_LIVE_URL;
};

const LOCAL_STANDALONE_WS_URL = 'ws://localhost:6345';

/**
 * The standalone service's REST base (session list and join), derived from its
 * websocket URL: nginx serves both under the same `/standalone/` route, so the
 * REST base is the websocket URL with the http scheme and no trailing slash
 * (`wss://dev.salesserver.net/standalone/` -> `https://dev.salesserver.net/standalone`).
 * No tenant needs a second env var.
 */
export const resolveStandaloneApiUrl = (standaloneWsUrl: string | undefined): string =>
  (standaloneWsUrl?.trim() || LOCAL_STANDALONE_WS_URL)
    .replace(/^ws(s?):\/\//i, 'http$1://')
    .replace(/\/+$/, '');

export const config = {
  server: {
    websocketPort: 6329,
    deepgramPort: 6331,
    landminePort: 6332,
    competitorPort: 6333,
    calendarPort: 6335,
    trainingPort: 6340,
    playbookLivePort: 6342,
    standalonePort: 6345,
    frontendPort: 3000,

    websocketurl: process.env.REACT_APP_WEBSOCKET_URL || 'ws://localhost:6329',
    deepgramUrl: process.env.REACT_APP_DEEPGRAM_URL || 'ws://localhost:6331',
    landmineUrl: process.env.REACT_APP_LANDMINE_URL || 'ws://localhost:6332',
    competitorUrl: process.env.REACT_APP_COMPETITOR_URL || 'ws://localhost:6333',
    calendarUrl: process.env.REACT_APP_CALENDAR_URL || 'ws://localhost:6335',
    trainingUrl: process.env.REACT_APP_TRAINING_URL || 'ws://localhost:6340',
    playbookLiveUrl: resolvePlaybookLiveUrl(
      process.env.REACT_APP_PLAYBOOK_LIVE_URL,
      process.env.REACT_APP_TRAINING_URL
    ),
    standaloneWsUrl: process.env.REACT_APP_STANDALONE_WS_URL || LOCAL_STANDALONE_WS_URL,
    standaloneApiUrl: resolveStandaloneApiUrl(process.env.REACT_APP_STANDALONE_WS_URL),
    playbookWsUrl: process.env.REACT_APP_PLAYBOOK_WS_URL ||
      (process.env.REACT_APP_STANDALONE_WS_URL?.includes('/standalone')
        ? process.env.REACT_APP_STANDALONE_WS_URL.replace(/\/standalone\/?$/, '/playbook/ws/client')
        : 'ws://localhost:6352/ws/client'),
    backendUrl: process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001',
    adminPortalUrl: process.env.REACT_APP_ADMIN_PORTAL_URL || 'http://localhost:3000',
  },
  app: {
    name: process.env.REACT_APP_NAME || 'Apexia Labs',
    version: process.env.REACT_APP_VERSION || '0.1.0',
    logLevel: 'INFO'
  },
  features: {
    // Gates the 'Playbook Name' label swap on the live-call surface only;
    // the playbookName data key is not flagged (the backend already emits
    // playbookName only). Default ON.
    playbookNameLabelEnabled: parseBooleanEnvVar(
      process.env.REACT_APP_PLAYBOOK_NAME_LABEL_ENABLED,
      true
    ),
  }
};
